/* Performance Analytics — dashboard host (alpaca_performance v8).
 *
 * The category → group → dashboard navigation lives in the app's OWN left
 * sidebar (the `grp-perfanalytics` nav group built from the manifest — see
 * layouts/navData.ts).  This component renders just the ONE selected dashboard
 * (`dashId`), or the Dashboard Catalog.  No second sidebar.  Demo data only. */
import { useEffect, useMemo, useRef, type ComponentType } from 'react';
import './performance.css';
import { FormTitle } from '../../components/FormTitle';
import { buildPages } from './pageData';
import { KpiRow, Blocks } from './parts';
import { AtRiskPage, RepScorecardPage, ClientContributionPage } from './specialPages';
import {
  ExecKpiPage, RecurrencePage, JobTatPage, DueDatePage, TatAlertsPage,
  RevenueExplorerPage, RevenueTrendPage, RevenueConcentrationPage,
} from './dashboardsV8';
import { Catalog } from './Catalog';

const SPECIAL: Record<string, ComponentType> = {
  'exec-kpi': ExecKpiPage,
  'lab-recurrence': RecurrencePage,
  'lab-tatdash': JobTatPage,
  'lab-duedatetracking': DueDatePage,
  'lab-tatalerts': TatAlertsPage,
  'revenue-explorer': RevenueExplorerPage,
  'revenue-trend': RevenueTrendPage,
  'revenue-concentration': RevenueConcentrationPage,
  'sales-atrisk': AtRiskPage,
  'sales-repscorecard': RepScorecardPage,
  'sales-clientcontribution': ClientContributionPage,
};

/** The Dashboard Catalog page — favorites come from the shared store (synced
 *  with the sidebar ⭐); clicking a card asks AppShell to open that dashboard. */
function CatalogHost({ onOpen }: { onOpen?: (id: string) => void }) {
  return (
    <div className="pa-root">
      <div className="pa-page-scroll">
        <Catalog onOpen={(id) => onOpen?.(id)} />
      </div>
    </div>
  );
}

export default function PerformanceAnalyticsPage({ dashId, onOpen }: { dashId: string; onOpen?: (id: string) => void }) {
  const pages = useMemo(buildPages, []);
  const scrollRef = useRef<HTMLDivElement>(null);
  useEffect(() => { if (scrollRef.current) scrollRef.current.scrollTop = 0; }, [dashId]);

  if (dashId === 'catalog') return <CatalogHost onOpen={onOpen} />;

  const Special = SPECIAL[dashId];
  const dash = pages[dashId];

  return (
    <div className="pa-root">
      <div className="pa-page-scroll" ref={scrollRef}>
        {Special ? (
          <Special />
        ) : dash ? (
          <>
            {/* Shared title (nav icon/colour + load-time chip) — single source of truth. */}
            <div className="pa-page-title">
              <FormTitle pageId={`pa-${dashId}`} title={dash.title} />
              {dash.badge && <span className={`pa-badge ${dash.badge}`}>{dash.badge === 'ai' ? 'AI' : 'New'}</span>}
            </div>
            <div className="pa-page-desc">{dash.desc}</div>
            <KpiRow kpis={dash.kpis} />
            <Blocks blocks={dash.blocks} />
          </>
        ) : (
          <div className="pa-page-desc">Dashboard not found.</div>
        )}
      </div>
    </div>
  );
}
